/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/roles-permissions/users-table/index.tsx (non-ecommerce, reason in screen-map §6)
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { Link, useLoaderData, useNavigate, useOutletContext, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import { PiCopyDuotone, PiHandshakeDuotone, PiHourglassMediumDuotone } from "react-icons/pi";
import { PagedTable, type StatusOption } from "../../components/table/data-table";
import { ListHeader } from "../../components/table/list-header";
import { SearchInput } from "../../components/table/search-input";
import { CaveatBanner, EmptyState } from "@steelyard/theme";
import { customerColumns } from "./columns";
import { CUSTOMER_STATUSES, Filters } from "./filters";
import { listCustomers } from "../../lib/services/customers";
import { listActiveTiers } from "../../lib/services/tier-options";
import { formatDate } from "../../lib/format";
import { hrefWith, readPaging } from "../../lib/paging";
import { Button } from "rizzui";
import { can } from "../../lib/permissions";
import type { PermissionState } from "../../types";

const ACTIVE_STATUS = CUSTOMER_STATUSES.find((option) => option.key === "active")!.value;

/**
 * Every figure is the API's, and none is compared to another — `creditLimit`/`outstanding` are
 * never subtracted here. `overLimit` (badged in `columns.tsx`) is likewise the API's own comparison
 * (`CustomerBalance.OverLimit`), landed as GAP-7 — see
 * `../../../../../specs/003-customers-tiers-credit/contracts/backend-gaps.md` GAP-7 and
 * `CreditBreachTile.tsx` for the dashboard-tile half of the same fix.
 */
export async function listLoader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const { page, pageSize } = readPaging(url.searchParams);
  const q = url.searchParams.get("q") ?? undefined;
  // Active is the default (project owner, 2026-09-29); `?status=all` is every customer. Only a
  // status this screen offers reaches the API: a hand-edited `?status=x` would otherwise go as NaN
  // and fail the whole page with a 400, where ignoring it falls back to the default.
  const rawStatus = url.searchParams.get("status");
  const status =
    rawStatus === "all" ? undefined : CUSTOMER_STATUSES.some((option) => String(option.value) === rawStatus) ? rawStatus! : ACTIVE_STATUS;
  const tierId = url.searchParams.get("tierId") ?? undefined;
  // Status counts: one `pageSize: 1` read per status, under the same search and tier, reading only
  // the API's own `totalCount`. A failed count costs its badge, not the list.
  const [data, tiers, ...statusTotals] = await Promise.all([
    listCustomers({ page, pageSize, q, status, tierId }),
    listActiveTiers(),
    ...CUSTOMER_STATUSES.map((option) =>
      listCustomers({ page: 1, pageSize: 1, q, status: option.value, tierId })
        .then((counted) => (counted ? Number(counted.totalCount) : null))
        .catch(() => null),
    ),
  ]);
  if (!data) throw new Error("The API returned no body for the customer list.");
  return { data, tiers, statusTotals, q, status, tierId };
}

export default function CustomersList() {
  const { data, tiers, statusTotals, q, status, tierId } = useLoaderData() as Awaited<ReturnType<typeof listLoader>>;
  // Each button below only for a role its screen admits (nav.tsx), never a link to a refusal.
  const { permissionState } = useOutletContext<{ permissionState: PermissionState }>();
  const [searchParams] = useSearchParams();
  const navigateTo = useNavigate();

  const stale = data.items.filter((row) => row.excludesReceipts);
  const lastApplied = stale.find((row) => row.receiptsLastAppliedAt)?.receiptsLastAppliedAt;

  const query = (over: Record<string, string | undefined>) => hrefWith("/customers", searchParams, over);
  // `filtered` is "differs from the default view" (Reset); `narrowed` is "hides any customer".
  const filtered = Boolean(q || status !== ACTIVE_STATUS || tierId);
  const narrowed = Boolean(q || status || tierId);

  const statusOptions: StatusOption[] = [
    { label: "All customers", href: query({ status: "all", page: undefined }), active: !status },
    ...CUSTOMER_STATUSES.map((option, index) => ({
      label: option.label,
      href: query({ status: option.value, page: undefined }),
      active: option.value === status,
      count: statusTotals[index],
      status: option.key,
    })),
  ];

  const table = (
    <PagedTable
      actions={
        <>
          {can(permissionState, "customers.credit") ? (
            <Link to="/customers/ageing">
              <Button as="span" variant="outline">
                <PiHourglassMediumDuotone aria-hidden className="me-1.5 size-4" />
                Ageing
              </Button>
            </Link>
          ) : null}
          {can(permissionState, "customers.terms") ? (
            <Link to="/customers/trade-terms">
              <Button as="span" variant="outline">
                <PiHandshakeDuotone aria-hidden className="me-1.5 size-4" />
                Trade terms
              </Button>
            </Link>
          ) : null}
          {can(permissionState, "customers.administer") ? (
            <Link to="/customers/dedupe">
              <Button as="span" variant="outline">
                <PiCopyDuotone aria-hidden className="me-1.5 size-4" />
                Duplicates
              </Button>
            </Link>
          ) : null}
        </>
      }
      status={{ label: "Status", options: statusOptions }}
      result={{
        items: data.items,
        page: Number(data.page),
        pageSize: Number(data.pageSize),
        totalCount: Number(data.totalCount),
        totalPages: data.totalPages === undefined ? undefined : Number(data.totalPages),
      }}
      caption="Customers, with their tier basis, credit limit and outstanding balance"
      columns={customerColumns}
      getRowId={(row) => row.customerId}
      // `q` is a server param, so this searches every customer, not the page on screen.
      search={<SearchInput value={q} placeholder="Search customers…" onSearch={(next) => navigateTo(query({ q: next, page: undefined }))} />}
      filters={<Filters tierId={tierId} tiers={tiers} query={query} />}
      clear={{ active: filtered, href: query({ q: undefined, status: undefined, tierId: undefined, page: undefined }) }}
      empty={
        <EmptyState
          title="No customers matched"
          action={
            narrowed ? (
              <Link to="/customers?status=all">
                <Button as="span" size="sm" variant="outline">
                  Clear the filters
                </Button>
              </Link>
            ) : null
          }
        >
          {narrowed
            ? "Nothing matched those filters. Clear them to see every customer."
            : "No customer has registered yet. The first one arrives through the portal's registration form."}
        </EmptyState>
      }
    />
  );

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Customers"
        count={Number(data.totalCount)}
        description="Registrations, tiers, credit limits and Tally ledger status."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Customers" }]}
      />
      {stale.length > 0 ? (
        <CaveatBanner
          caveat={`${stale.length} of these outstanding figures exclude payments received. Those customers may already have paid.`}
          action={
            <p>
              Receipts last applied: {lastApplied ? formatDate(lastApplied) : "never"}.{" "}
              {can(permissionState, "integrations.read") ? (
                <Link to="/integrations/tally" className="underline">
                  Check the Tally connector agent
                </Link>
              ) : (
                "Check the Tally connector agent"
              )}{" "}
              — receipts stop arriving when it stops running.
            </p>
          }
        >
          {table}
        </CaveatBanner>
      ) : (
        table
      )}
    </div>
  );
}
